'use client';

import { useState, type FormEvent } from 'react';
import { getAttribution } from '@/lib/analytics/attribution';
import { useRouter, usePathname } from 'next/navigation';
import { Globe, Check, ArrowRight, Shield, Zap, FileText } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/Button';
import { AuditProgressNotification } from './AuditProgressNotification';
import styles from './AuditLanding.module.css';

export function AuditLanding() {
  const t = useTranslations('auditLanding');
  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname.split('/')[1] || 'en';

  const [domain, setDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditUrl, setAuditUrl] = useState<string | null>(null);

  const fullUrl = domain.trim() ? `https://${domain.trim()}` : '';

  const handleCancel = () => {
    setIsLoading(false);
    setAuditUrl(null);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!domain.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);
    setAuditUrl(fullUrl);

    try {
      const response = await fetch('/api/audit/free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: fullUrl, attribution: getAttribution() }),
      });

      const data = await response.json();

      if (data.success && data.reportId) {
        router.push(`/${locale}/audit/${data.reportId}${data.accessToken ? `?t=${encodeURIComponent(data.accessToken)}` : ''}`);
      } else {
        setError(
          data.code === 'UNRESOLVABLE_HOST' ? t('unresolvableDomain') : data.error || 'Audit failed'
        );
        setAuditUrl(null);
      }
    } catch {
      setError('Network error. Please try again.');
      setAuditUrl(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.landing}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <span className={styles.label}>{t('hero.label')}</span>
          <h1 className={styles.title}>{t('hero.title')}</h1>
          <p className={styles.subtitle}>{t('hero.subtitle')}</p>

          {/* URL Input Form */}
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputRow}>
              <div className={styles.inputWrapper}>
                <Globe size={18} className={styles.inputIcon} />
                <span className={styles.inputPrefix}>https://</span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="example.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value.replace(/^https?:\/\//, ''))}
                  required
                  autoComplete="off"
                  disabled={isLoading}
                  aria-label={t('hero.inputLabel')}
                />
              </div>
              <Button type="submit" size="lg" disabled={isLoading || !domain.trim()}>
                {isLoading ? t('hero.loading') : t('hero.button')}
              </Button>
            </div>

            <AuditProgressNotification
              isVisible={isLoading}
              url={auditUrl || undefined}
              tier="free"
              error={error || undefined}
              locale={locale}
              onCancel={handleCancel}
            />

            {!isLoading && error && <p className={styles.error} role="alert">{error}</p>}
            {!isLoading && <p className={styles.hint}>{t('hero.hint')}</p>}
          </form>

          {/* Stats */}
          <div className={styles.stats}>
            <div className={styles.stat}>
              <span className={styles.statValue}>167</span>
              <span className={styles.statLabel}>{t('hero.stats.principles')}</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statValue}>12</span>
              <span className={styles.statLabel}>{t('hero.stats.citations')}</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statValue}>&lt;30s</span>
              <span className={styles.statLabel}>{t('hero.stats.speed')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className={styles.section}>
        <div className={styles.container}>
          <span className={styles.label}>{t('whatYouGet.label')}</span>
          <h2 className={styles.sectionTitle}>{t('whatYouGet.title')}</h2>

          <div className={styles.tiers}>
            {/* Free Tier */}
            <div className={styles.tier}>
              <div className={styles.tierHeader}>
                <Zap size={20} className={styles.tierIcon} />
                <h3 className={styles.tierName}>{t('whatYouGet.free.name')}</h3>
                <span className={styles.tierPrice}>{t('whatYouGet.free.price')}</span>
              </div>
              <p className={styles.tierDesc}>{t('whatYouGet.free.description')}</p>
              <ul className={styles.tierFeatures}>
                {(['f1', 'f2', 'f3', 'f4', 'f5'] as const).map((key) => (
                  <li key={key} className={styles.feature}>
                    <Check size={16} className={styles.featureCheck} />
                    <span>{t(`whatYouGet.free.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pro Tier */}
            <div className={`${styles.tier} ${styles.tierPro}`}>
              <div className={styles.tierHeader}>
                <FileText size={20} className={styles.tierIconPro} />
                <h3 className={styles.tierName}>{t('whatYouGet.pro.name')}</h3>
                <span className={styles.tierPricePro}>{t('whatYouGet.pro.price')}</span>
              </div>
              <p className={styles.tierDesc}>{t('whatYouGet.pro.description')}</p>
              <ul className={styles.tierFeatures}>
                {(['f1', 'f2', 'f3', 'f4', 'f5', 'f6'] as const).map((key) => (
                  <li key={key} className={styles.feature}>
                    <Check size={16} className={styles.featureCheckPro} />
                    <span>{t(`whatYouGet.pro.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={styles.section}>
        <div className={styles.container}>
          <span className={styles.label}>{t('howItWorks.label')}</span>
          <h2 className={styles.sectionTitle}>{t('howItWorks.title')}</h2>

          <div className={styles.steps}>
            {([1, 2, 3] as const).map((step) => (
              <div key={step} className={styles.step}>
                <div className={styles.stepNumber}>{step}</div>
                <div className={styles.stepContent}>
                  <h3 className={styles.stepTitle}>{t(`howItWorks.step${step}.title`)}</h3>
                  <p className={styles.stepDesc}>{t(`howItWorks.step${step}.description`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className={styles.section}>
        <div className={styles.container}>
          <span className={styles.label}>{t('trust.label')}</span>
          <h2 className={styles.sectionTitle}>{t('trust.title')}</h2>

          <div className={styles.trustGrid}>
            <div className={styles.trustItem}>
              <Shield size={24} className={styles.trustIcon} />
              <h3 className={styles.trustItemTitle}>{t('trust.noSignup.title')}</h3>
              <p className={styles.trustItemDesc}>{t('trust.noSignup.description')}</p>
            </div>
            <div className={styles.trustItem}>
              <Zap size={24} className={styles.trustIcon} />
              <h3 className={styles.trustItemTitle}>{t('trust.instant.title')}</h3>
              <p className={styles.trustItemDesc}>{t('trust.instant.description')}</p>
            </div>
            <div className={styles.trustItem}>
              <FileText size={24} className={styles.trustIcon} />
              <h3 className={styles.trustItemTitle}>{t('trust.research.title')}</h3>
              <p className={styles.trustItemDesc}>{t('trust.research.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className={styles.bottomCta}>
        <div className={styles.container}>
          <h2 className={styles.ctaTitle}>{t('cta.title')}</h2>
          <p className={styles.ctaSubtitle}>{t('cta.subtitle')}</p>
          <div className={styles.ctaActions}>
            <Button size="lg" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              {t('cta.primary')}
              <ArrowRight size={18} />
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => router.push(`/${locale}/demo-report`)}
            >
              {t('cta.secondary')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
